import { IOrderItem } from "interfaces";
import { SwitchButton } from "components/switchButton";
import { Link } from "react-router-dom";
import Button from "components/button";
import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { formatNumber, getOrderStatusLabel, getPaymentMethodLabel, getTicketPlatformLabel, isHaveActionPermission } from "utills/helpers";
import { Tag } from "components/tag";
import moment from "moment";

interface OrderCardProps {
  data?: IOrderItem[];
  pageSize: number;
  currentPage: number;
}

export const OrderCard = ({
  data = [],
  pageSize = 10,
  currentPage = 1,
}: OrderCardProps) => {
  return (
    <ul className="bg-white">
      {data.map((item: IOrderItem, index: number) => (
        <div
          key={index}
          className="flex flex-col gap-y-4 overflow-hidden shadow-md ring-1 ring-gray-900/5 [&:not(:last-child)]:mb-4 p-4 rounded-xl"
        >
          <div className="w-full flex items-center justify-end">
            <div className="flex items-center gap-x-[8px]">
              <Link to={`/orders/${item.code}`} className="w-fit">
                <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                  <EyeIcon className="w-4 h-4 text-black" />
                </Button>
              </Link>
              {isHaveActionPermission("", "orders", "edit") ? (
                <Link
                  to={`/orders/${item.code}/edit`}
                  className="w-fit"
                >
                  <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                    <PencilIcon className="w-4 h-4 text-black" />
                  </Button>
                </Link>
              ) : <></>}
            </div>
          </div>

          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">No</p>
            <p className="flex-1 text-right text-sm leading-6">
              {(currentPage - 1) * pageSize + 1 + index}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Order code</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.code}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Group name/Buyer email & phone</p>
            <div className="flex-1 text-right text-sm leading-6">
              {item.group_name ? <p className="font-semibold">{item.group_name}</p> : <></>}
              <p>{item.buyer_email}</p>
              <p>{item.buyer_phone}</p>
            </div>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Ticket name</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.item_description_en}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Event name</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.event_name_en}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">VAT</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.is_vat ? "Yes" : "No"}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Payment Method</p>
            <p className="flex-1 text-right text-sm leading-6">
              {getPaymentMethodLabel(item.payment_method)}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Status</p>
            <div className="flex-1 text-right text-sm leading-6">
              <Tag text={getOrderStatusLabel(item.order_status)} type={item.order_status}  />
            </div>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Coupon code</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.coupon_codes.join(",")}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Created at</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.created_at ? moment(item.created_at).format("DD/MM/YYYY HH:mm:ss") : ""}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Payment at</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.purchased_at ? moment(item.purchased_at).format("DD/MM/YYYY HH:mm:ss") : ""}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">E-ticket usage date</p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.ticket_use_for_date ? moment(item.ticket_use_for_date).format("DD/MM/YYYY") : ""}
              {item.showtime ? ` - ${item.showtime}` : ""}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">Ticket platform</p>
            <p className="flex-1 text-right text-sm leading-6">
              {getTicketPlatformLabel(item.ticket_platform)}
            </p>
          </li>
        </div>
      ))}
    </ul>
  );
};
